/**
 * Manually scrolls an element into view within a specific scrollable container.
 * This prevents the browser from scrolling ancestor containers on older devices.
 * Mimics scrollIntoView with block: "nearest" behavior, with added buffer for safety.
 * 
 * @param {HTMLElement} element - The element to scroll into view
 * @param {string} scrollContainerClass - The class name of the scrollable container (e.g., 'scrollingRows', 'eventsGridContainer')
 * @param {number} buffer - Extra buffer in pixels to ensure element is fully visible (default: 200px)
 */

export const scrollIntoViewWithinContainer = (element, scrollContainerClass, buffer = 200) => {
    if (!element) return;

    // Find the scrollable container by traversing up the DOM
    let scrollContainer = element.parentElement;
    while (scrollContainer && !scrollContainer.classList.contains(scrollContainerClass)) {
        scrollContainer = scrollContainer.parentElement;
    }
    if (!scrollContainer) {
        return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    // Check if element is outside the visible area (with buffer)
    const isAbove = elementRect.top < (containerRect.top + buffer);
    const isBelow = elementRect.bottom > (containerRect.bottom - buffer);

    if (isAbove) {
        // Element is above viewport or too close to top - scroll to align element top with container top + buffer
        const offset = elementRect.top - containerRect.top - buffer;
        const newScrollTop = scrollContainer.scrollTop + offset;
        scrollContainer.scrollTop = Math.max(0, newScrollTop); // Don't scroll past the top
    } else if (isBelow) {
        // Element is below viewport or too close to bottom - scroll to align element bottom with container bottom - buffer
        const offset = elementRect.bottom - containerRect.bottom + buffer;
        const newScrollTop = scrollContainer.scrollTop + offset;
        const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        scrollContainer.scrollTop = Math.min(maxScroll, newScrollTop); // Don't scroll past the bottom
    }
};
